var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTheme } from "styled-components";
import { Checkbox } from "../../Checkbox";
import { Box } from "../../Box";
import { Flex } from "../../Flex";
import { useUniqueId } from "../../../util/useUniqueId";
import { HelperText } from "../FormFieldWrapper/FormFieldWrapper";
import { FieldErrorMessage } from "../FieldErrorMessage/FieldErrorMessage";
import { FormFieldGroupContext } from "../FormFieldGroup";
import { GenericFormContext } from "../GenericFormContext";
/**
 * useCheckedFromDefaultValues checks the default checked state by checking the value from the form field value path name is true, otherwise returns the defaultChecked Boolean
 * @param name - The path name to the form field value
 * @param defaultChecked - Boolean for default checked state
 * @returns a Boolean representing checked state
 */
export const useCheckedFromDefaultValues = (name, defaultChecked) => {
    const { getValues } = useFormContext();
    const formValue = getValues(name);
    if (formValue !== undefined) {
        return Boolean(formValue === "true" || formValue === true);
    }
    return Boolean(defaultChecked);
};
/**
 * Used to convert the string `"true"` to a boolean, to support both values equally.
 * All other strings will be forced to `false`.
 */
const forceBoolean = (value) => {
    if (value === "true" || value === true) {
        return true;
    }
    return false;
};
/**
 * When we `setValue` manually we don't want to make the field dirty,
 * `react-hook-form` will manage the dirty on its own.
 */
const shouldDirty = false;
/**
 * <br /> <br />
 * ## Description
 *
 * The FormCheckbox component is a wrapper that integrates the Checkbox component into forms. <br>
 * It allows developers to include checkbox input fields with associated labels, helper text,
 * and optional disabling or read-only mode. <br>
 * The component handles form submission and provides functionality for managing the checked state of the checkbox.
 */
export const FormCheckbox = (_a) => {
    var { defaultChecked = false, disabled, helperText, label, name, readOnly = false, registerProps, required } = _a, rest = __rest(_a, ["defaultChecked", "disabled", "helperText", "label", "name", "readOnly", "registerProps", "required"]);
    const { formState, getFieldState, register, setValue, watch } = useFormContext();
    const { colors } = useTheme();
    const rawValue = watch(name);
    const forcedValue = forceBoolean(rawValue);
    useEffect(() => {
        if (defaultChecked) {
            setValue(name, true, {
                shouldDirty,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * When the value in form state changes set our `forcedValue` instead.
     * Don't do this for `undefined` to ensure the `reset` method works as expected.
     */
    useEffect(() => {
        if (rawValue !== undefined) {
            setValue(name, forcedValue, {
                shouldDirty: true,
            });
        }
    }, [rawValue, forcedValue, setValue, name]);
    const descriptorId = useUniqueId();
    const { error } = getFieldState(name, formState);
    const { editIsAllowed } = useContext(GenericFormContext);
    const isReadOnly = readOnly === true || editIsAllowed === false;
    const { disabled: disabledFromFormFieldGroup } = React.useContext(FormFieldGroupContext);
    const disabledState = disabledFromFormFieldGroup || disabled;
    return (React.createElement(Flex, { flexWrap: "wrap", alignItems: "flex-start", flexDirection: "column", pb: 3 },
        React.createElement(Flex, { as: "label" },
            React.createElement(Checkbox
            // Will return onChange and OnBlur functions handled by React Hook Form
            , Object.assign({}, register(name, Object.assign({ disabled: disabledState, 
                // When the value in Form state is `undefined`, pass it explicitly to ensure `react-hook-form` methods work correctly (eg. `reset`)
                value: rawValue === undefined ? undefined : forcedValue }, registerProps)), { checked: forcedValue, containerProps: { top: "2px" }, descriptorId: helperText && descriptorId, readOnly: isReadOnly }, rest)),
            React.createElement(Box, { as: "span", ml: 1 },
                label,
                (required || (registerProps === null || registerProps === void 0 ? void 0 : registerProps.required)) && (React.createElement(Box, { as: "span", color: colors.formFieldWrapperColors.requiredStar, "data-testid": `FormFieldRequired-${name}` }, "*")))),
        React.createElement(Box, { "data-testid": `FormFieldChildren-${name}`, flex: 2 },
            React.createElement(FieldErrorMessage, { name: name, error: error === null || error === void 0 ? void 0 : error.message }),
            helperText && React.createElement(HelperText, { id: descriptorId }, helperText))));
};
